import {useCallback, useRef} from 'react';

/**
 * Wraps a "fetch next page" function with a synchronous deduplication guard
 * so that rapid/throttled scroll events cannot trigger duplicate requests.
 *
 * Uses a ref so the guard is checked before React re-renders with updated
 * isFetching state, avoiding stale closure issues.
 *
 * @param fetchNextPage - The underlying fetch function (e.g. from useInfiniteQuery)
 * @param isFetching - Current fetching state from the query
 * @returns A guarded function that skips when a request is already in flight
 */
function useGuardedFetchNextPage<TResult = unknown>(
  fetchNextPage: () => Promise<TResult>,
  isFetching: boolean
): () => void {
  const isFetchingNextPageRef = useRef(false);

  return useCallback(() => {
    if (isFetchingNextPageRef.current || isFetching) {
      return;
    }
    isFetchingNextPageRef.current = true;
    fetchNextPage()
      .finally(() => {
        isFetchingNextPageRef.current = false;
      })
      .catch(() => {}); // Swallow rejection so the chain is never unhandled
  }, [fetchNextPage, isFetching]);
}

export default useGuardedFetchNextPage;
